// src/pages/PortalLogin.jsx
import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Building2,
  Eye,
  EyeOff,
  ShieldCheck,
} from "lucide-react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { supabase } from "../../supabaseClient";
import {
  getPartnerAccessMessage,
  resolvePostLoginDestination,
} from "../../lib/access/portalAccess";

const PORTAL_CONFIG = {
  admin: {
    eyebrow: "Gwin",
    title: "Admin Dashboard",
    description:
      "Sign in using your approved Gwin administrator account.",
    notice:
      "This portal is only available to approved Gwin administrators.",
    loginLabel: "Sign in as admin",
    Icon: ShieldCheck,
  },

  partner: {
    eyebrow: "Gwin",
    title: "Partner Portal",
    description:
      "Manage your organisation, cohorts, learners, and reports.",
    notice:
      "Partner accounts are invitation-only. Contact Gwin if your organisation needs access.",
    loginLabel: "Sign in as partner",
    Icon: Building2,
  },
};

export default function PortalLogin({ portal = "partner" }) {
  const config = PORTAL_CONFIG[portal] || PORTAL_CONFIG.partner;
  const Icon = config.Icon;

  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [routing, setRouting] = useState(false);
  const [error, setError] = useState("");

  const queryAccessMessage = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return getPartnerAccessMessage(params.get("access"));
  }, [location.search]);

  const routeAuthenticatedUser = async (userId) => {
    if (!userId) return;

    try {
      setRouting(true);
      setError("");

      const result =
        await resolvePostLoginDestination({
          userId,
          preferredPortal: portal,
        });

      const destination = result?.destination || "/login";
      const currentPath = `${location.pathname}${location.search}`;

      if (destination === currentPath) {
        setError(
          getPartnerAccessMessage(result?.access?.access_status) ||
            "You do not currently have access to this portal."
        );
        return;
      }

      navigate(destination, {
        replace: true,
      });
    } catch (err) {
      console.error("Portal routing failed:", err);

      setError(
        err?.message ||
          "We could not verify your portal access."
      );
    } finally {
      setRouting(false);
    }
  };

  useEffect(() => {
    if (authLoading || !user?.id) return;

    routeAuthenticatedUser(user.id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);
      setError("");

      const cleanEmail = email.trim().toLowerCase();

      if (
        !cleanEmail ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)
      ) {
        throw new Error("Please enter a valid email address.");
      }

      if (!password) {
        throw new Error("Please enter your password.");
      }

      const {
        data,
        error: signInError,
      } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password,
      });

      if (signInError) throw signInError;

      const signedInUser = data?.user;

      if (!signedInUser?.id) {
        throw new Error(
          "The account was signed in, but the user could not be loaded."
        );
      }

      await routeAuthenticatedUser(signedInUser.id);
    } catch (err) {
      setError(
        err?.message ||
          "Unable to sign in. Please check your details."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const busy =
    authLoading ||
    submitting ||
    routing;

  return (
    <div className="min-h-screen bg-[#FFF7ED] px-5 py-8 font-sans text-[#333333] sm:px-6">
      <div className="mx-auto w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold text-[#757575] transition hover:text-[#FF914D]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Gwin
        </Link>

        <section className="mt-7 overflow-hidden rounded-[30px] border border-[#FFE1CF] bg-white shadow-sm">
          <div className="bg-[#FF914D] px-6 py-8 text-white">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20">
              <Icon className="h-7 w-7" />
            </div>

            <p className="mt-6 text-xs font-black uppercase tracking-[0.18em] text-white/80">
              {config.eyebrow}
            </p>

            <h1 className="mt-2 text-3xl font-black tracking-[-0.03em]">
              {config.title}
            </h1>

            <p className="mt-3 text-sm leading-relaxed text-white/90">
              {config.description}
            </p>
          </div>

          <div className="p-6 sm:p-7">
            <div className="rounded-2xl border border-[#FFE1CF] bg-[#FFF7ED] p-4 text-sm leading-relaxed text-[#75523B]">
              {config.notice}
            </div>

            {(queryAccessMessage || error) && (
              <div
                role="alert"
                className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
              >
                {error || queryAccessMessage}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="mt-7 space-y-5"
            >
              <div>
                <label
                  htmlFor={`${portal}-email`}
                  className="block text-sm font-black text-[#333333]"
                >
                  Email address
                </label>

                <input
                  id={`${portal}-email`}
                  type="email"
                  value={email}
                  onChange={(event) =>
                    setEmail(event.target.value)
                  }
                  disabled={busy}
                  autoComplete="email"
                  inputMode="email"
                  placeholder="Enter your email"
                  className="mt-2 w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm font-semibold text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15 disabled:opacity-60"
                />
              </div>

              <div>
                <label
                  htmlFor={`${portal}-password`}
                  className="block text-sm font-black text-[#333333]"
                >
                  Password
                </label>

                <div className="relative mt-2">
                  <input
                    id={`${portal}-password`}
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    value={password}
                    onChange={(event) =>
                      setPassword(event.target.value)
                    }
                    disabled={busy}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 pr-12 text-sm font-semibold text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15 disabled:opacity-60"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword((current) => !current)
                    }
                    disabled={busy}
                    className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-[#757575] transition hover:bg-[#FFF4EC] hover:text-[#FF914D]"
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm font-bold text-[#FF914D] hover:text-[#E9782F]"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-full bg-[#FF914D] px-5 py-3.5 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy
                  ? "Checking access…"
                  : config.loginLabel}
              </button>
            </form>

            <div className="mt-7 border-t border-[#EFEFEF] pt-5 text-center">
              <p className="text-sm text-[#757575]">
                Looking for the learner app?
              </p>

              <Link
                to="/login"
                className="mt-2 inline-flex text-sm font-black text-[#FF914D] hover:text-[#E9782F]"
              >
                Open learner login
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}